import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { StudentWorkPlacementJourneyStatus } from '@duke-hq/svc-api-db/client';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import {
  type ChangeableStage,
  changeableStages,
  isChangeableStage,
  journeyStages,
  stageLabel,
} from './journeyStages';
import { testIds } from './testIds';

export function canChangeStage({
  journeyStatus,
  onHold = false,
}: {
  journeyStatus: StudentWorkPlacementJourneyStatus | null;
  onHold?: boolean;
}): boolean {
  if (journeyStatus == null || onHold) {
    return false;
  }
  return changeableStages(journeyStatus).length > 0;
}

interface FormData {
  stage: ChangeableStage | null;
  reason: string;
}

export function ChangeStageModal({
  isOpen,
  onClose,
  journeyId,
  currentStatus,
  initialStage,
  onJourneyChanged,
}: {
  isOpen: boolean;
  onClose: () => void;
  journeyId: string;
  currentStatus: StudentWorkPlacementJourneyStatus;
  initialStage?: ChangeableStage | null;
  onJourneyChanged: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const stages = useMemo(() => changeableStages(currentStatus), [currentStatus]);
  const openingStage = initialStage != null && stages.includes(initialStage) ? initialStage : null;
  const fromLabel = stageLabel(currentStatus);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: { stage: openingStage, reason: '' },
  });

  const wasOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      reset({ stage: openingStage, reason: '' });
    }
    wasOpen.current = isOpen;
  }, [isOpen, openingStage, reset]);

  const selectedStage = watch('stage');

  const setStageMutation = trpcAdmin.workPlacements.placements.setJourneyStage.useMutation({
    onSuccess: ({ journeyStatus, atRequestedStage }) => {
      toast(
        atRequestedStage
          ? { status: 'success', title: t`Stage updated` }
          : {
              status: 'warning',
              title: t`Stage set to ${stageLabel(journeyStatus) ?? journeyStatus}`,
              description: t`The placement's own progress decides this stage, so it could not move where you asked.`,
            },
      );
      onJourneyChanged();
      onClose();
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to update stage`, description: error.message });
      if (error.data?.code === 'CONFLICT') {
        setValue('stage', null);
        onJourneyChanged();
      }
    },
  });

  const onSubmit = (data: FormData) => {
    if (data.stage == null) {
      return;
    }
    setStageMutation.mutate({
      journeyId,
      toStage: data.stage,
      reason: data.reason,
    });
  };

  return (
    <FormModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      modalContentHeight="auto"
      modalContent={
        <FormMainContainer>
          <FormContentContainer>
            <Text fontSize="lg" fontWeight="semibold" w="full">
              <Trans>Change stage</Trans>
            </Text>
            {selectedStage != null && fromLabel != null ? (
              <Text fontSize="sm" color="fg.muted" w="full">
                {fromLabel}
                {' → '}
                {stageLabel(selectedStage)}
              </Text>
            ) : null}

            <FormControl isRequired>
              <FormLabel>
                <Trans>Stage</Trans>
              </FormLabel>
              <Controller
                control={control}
                name="stage"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value ?? ''}
                    onChange={(value) => {
                      if (isChangeableStage(value)) {
                        field.onChange(value);
                      }
                    }}
                  >
                    <Stack gap={2}>
                      {journeyStages().map((stage) => {
                        const selectable =
                          stage.journeyStatus != null && stage.journeyStatus !== currentStatus;
                        const isCurrent = stage.journeyStatus === currentStatus;

                        return (
                          <Box
                            key={stage.key}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={2}
                            px={4}
                            py={3}
                            borderWidth="1px"
                            borderRadius="md"
                            borderColor={
                              field.value === stage.journeyStatus ? 'purple.500' : 'border.default'
                            }
                            bg={selectable ? 'bg.canvas' : 'bg.subtle'}
                          >
                            <Radio
                              value={stage.journeyStatus ?? stage.key}
                              colorScheme="purple"
                              isDisabled={!selectable}
                            >
                              {stage.label}
                            </Radio>
                            {isCurrent ? (
                              <Text fontSize="xs" color="fg.muted">
                                <Trans>Current</Trans>
                              </Text>
                            ) : null}
                          </Box>
                        );
                      })}
                    </Stack>
                  </RadioGroup>
                )}
              />
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.reason}>
              <FormLabel>
                <Trans>Reason</Trans>
              </FormLabel>
              <Textarea
                {...register('reason', { required: t`Please provide a reason for this change` })}
                placeholder={t`Explain why the stage is changing`}
                rows={3}
              />
              {errors.reason ? <FormErrorMessage>{errors.reason.message}</FormErrorMessage> : null}
            </FormControl>
          </FormContentContainer>

          <FormFooterContainer>
            <Button
              isLoading={setStageMutation.isPending}
              isDisabled={selectedStage == null}
              onClick={handleSubmit(onSubmit)}
              data-testid={testIds.changeStageConfirm}
            >
              <Trans>Confirm</Trans>
            </Button>
            <Button variant="ghost" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
          </FormFooterContainer>
        </FormMainContainer>
      }
    />
  );
}
